import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

export function useScrollToActiveCard(
    activeEventId: string | null,
    isLoading: boolean,
    itemCount: number,
    scrollContainerRef: RefObject<HTMLDivElement | null>,
) {
    const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const lastScrolledEventId = useRef<string | null>(null);

    const setCardRef = useCallback(
        (eventId: string) => (el: HTMLDivElement | null) => {
            if (el) {
                cardRefs.current.set(eventId, el);
            } else {
                cardRefs.current.delete(eventId);
            }
        },
        [],
    );

    useEffect(() => {
        if (isLoading || itemCount === 0 || !activeEventId) return;
        if (lastScrolledEventId.current === activeEventId) return;

        const frameId = requestAnimationFrame(() => {
            const el = cardRefs.current.get(activeEventId);
            const container = scrollContainerRef.current;
            if (!el || !container) return;

            const cardTop = el.offsetTop;
            const cardBottom = cardTop + el.clientHeight;
            const viewTop = container.scrollTop;
            const viewBottom = viewTop + container.clientHeight;
            if (cardTop >= viewTop && cardBottom <= viewBottom) return;

            lastScrolledEventId.current = activeEventId;

            const targetTop =
                cardTop - container.clientHeight / 2 + el.clientHeight / 2;
            container.scrollTo({
                top: Math.max(0, targetTop),
                behavior: 'smooth',
            });
        });

        return () => cancelAnimationFrame(frameId);
    }, [isLoading, itemCount, activeEventId, scrollContainerRef]);

    return { setCardRef };
}
